/**
 * Content signature for a lesson's quiz/flashcard set.
 *
 * A persisted practice session is only valid against the exact content it was
 * started on. When the artifact is regenerated, the signature changes and the
 * stored session is discarded (so we never restore stale answers against new
 * questions). Combines the artifact `version` with a hash of the item text so
 * it changes even if a regeneration reuses the same version string.
 */

/** FNV-1a 32-bit hash → base36. Small, dependency-free, good enough for a
 *  change-detection fingerprint (not security). */
function fnv1a(str: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/** Signature from the artifact version + item count + a hash of item texts. */
export function contentSignature(
  version: string | undefined,
  texts: string[]
): string {
  // Join with a control char that won't occur in content, so distinct item
  // sets that would otherwise concatenate to the same string don't collide.
  return `${version ?? ''}:${texts.length}:${fnv1a(texts.join(''))}`;
}

/**
 * The signature of a quiz or deck from its written items only. Items made
 * from a concept's picture (`from_visual` / `fromVisual`) are appended after
 * the written ones when the picture is ready - maybe mid-session - and must
 * not reset a session in progress.
 */
export function writtenSignature<
  T extends { from_visual?: boolean; fromVisual?: boolean },
>(items: T[], text: (item: T) => string): string {
  return contentSignature(
    undefined,
    items.filter(i => !i.from_visual && !i.fromVisual).map(text)
  );
}
